import React, {FC} from "react";
import {Link} from "react-router-dom";
import {formatNumber, getFiat} from "../../../common/utils/function";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";

interface InfoData {
    extra?: any;
    stats?: any;
}

const Info: FC<InfoData> = (props) => {
    const {t} = useTranslation();
    const fiat = useSelector((e: Object) => (e as any)?.common?.fiat) || "usd";

    const coingeckoData = props?.stats?.coingecko_data;
    const marketData = coingeckoData?.market_data || {};
    // const tickers = coingeckoData?.tickers || [];
    // const totalVolume = marketData.total_volume?.usd || 1; // Default to 1 to prevent division by zero

    return (
        <div className="row">
            <div className="col-lg-12">
                <div className="bs-card h-100">
                    {coingeckoData?.description ? (
                        <div className="bs-card-body p-3">
                            <div className="d-flex align-items-center justify-content-between pb-1">
                                <h2 className="bs-card-header-title">{t("Overview")}</h2>
                            </div>
                            <div className="pb-3">
                                <span>{coingeckoData.description.en}</span>
                            </div>
                            {props?.extra?.rwa && (
                                <div className="bs-card-body p-4">
                                    <div className="d-flex align-items-center justify-content-between pb-1">
                                        <h2 className="bs-card-header-title">{t("Type of Backing")}</h2>
                                    </div>
                                    <div className="pb-3">
                                        <span>{props?.extra?.rwa?.type}</span>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between pb-1">
                                        <h2 className="bs-card-header-title">{t("Type of Peg")}</h2>
                                    </div>
                                    <div className="pb-3">
                                        <span>{props?.extra?.rwa?.peg}</span>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between pb-1">
                                        <h2 className="bs-card-header-title">{t("Collater­alization")}</h2>
                                    </div>
                                    <div className="pb-3">
                                        <span>{props?.extra?.rwa?.collate}</span>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between pb-1">
                                        <h2 className="bs-card-header-title">{t("Storage Of Assets")}</h2>
                                    </div>
                                    <div className="pb-3">
                                        <span>{props?.extra?.rwa?.storageOfAssets}</span>
                                    </div>
                                </div>
                            )}

                            <div className="d-flex align-items-center justify-content-between pb-1">
                                <h2 className="bs-card-header-title">{t("Market")}</h2>
                            </div>
                            <div className="pb-2">
                                <p>
                                    {t("Volume (24H)")} : &nbsp;
                                    {getFiat(marketData.total_volume?.usd ?? 0, 6, fiat, true)}
                                </p>
                                <p>
                                    {t("Market Capitalization")} :{" "}
                                    {getFiat(marketData.market_cap?.usd ?? 0, 6, fiat, true)}
                                </p>
                                <p>
                                    {t("Market Cap Rank")} :{" "}
                                    {marketData.market_cap_rank}
                                </p>
                                <p>
                                    {t("Price Change (24H)")} :{" "}
                                    {marketData.price_change_percentage_24h?.toFixed(2)}%
                                </p>
                                <p>
                                    {t("Price Change (7D)")} :{" "}
                                    {marketData.price_change_percentage_7d?.toFixed(2)}%
                                </p>
                                <p>
                                    {t("Price Change (30D)")} :{" "}
                                    {marketData.price_change_percentage_30d?.toFixed(2)}%
                                </p>
                                <p>
                                    {t("Price Change (1Y)")} :{" "}
                                    {marketData.price_change_percentage_1y?.toFixed(2)}%
                                </p>
                                <p>
                                    {t("Circulating Supply")} :{" "}
                                    {formatNumber(marketData.circulating_supply ?? 0)} {props?.stats?.symbol}
                                </p>
                            </div>

                            <div className="d-flex align-items-center justify-content-between pb-1">
                                <h2 className="bs-card-header-title">{t("Additional Information")}</h2>
                            </div>
                            <div className="pb-2">
                                <p>
                                    {t("Total Supply")} :{" "}
                                    {formatNumber(marketData.total_supply ?? 0)} {props?.stats?.symbol}
                                </p>
                                <p>
                                    {t("Max Supply")} :{" "}
                                    {formatNumber(marketData.max_supply ?? 0)} {props?.stats?.symbol}
                                </p>
                                <p>
                                    {t("Decimals")} :{" "}
                                    {props?.stats?.decimals}
                                </p>
                                <p>
                                    {t("Fiat Value")} :{" "}
                                    {getFiat(props?.stats?.fiat_value ?? 0, 6, fiat, true)}
                                </p>
                                <p>
                                    {t("Price Change (24H)")} :{" "}
                                    {props?.stats?.price_change_24h}%
                                </p>
                            </div>

                            <div className="d-flex align-items-center justify-content-between pb-1">
                                <h2 className="bs-card-header-title">{t("Community & Links")}</h2>
                            </div>
                            <div className="pb-2">
                                {coingeckoData?.links?.homepage?.[0] && (
                                    <p>
                                        {t("Website")} :{" "}
                                        <a href={coingeckoData?.links?.homepage[0]} target="_blank" rel="noopener noreferrer">
                                            {coingeckoData?.links?.homepage[0]}
                                        </a>
                                    </p>
                                )}
                                {coingeckoData?.links?.whitepaper && (
                                    <p>
                                        {t("Whitepaper")} :{" "}
                                        <a href={coingeckoData?.links?.whitepaper} target="_blank" rel="noopener noreferrer">
                                            {coingeckoData?.links?.whitepaper}
                                        </a>
                                    </p>
                                )}
                                {coingeckoData?.links?.twitter_screen_name && (
                                    <p>
                                        {t("Twitter")} :{" "}
                                        <a href={`https://twitter.com/${coingeckoData?.links?.twitter_screen_name}`} target="_blank" rel="noopener noreferrer">
                                            @{coingeckoData?.links?.twitter_screen_name}
                                        </a>
                                    </p>
                                )}
                                {coingeckoData?.links?.telegram_channel_identifier && (
                                    <p>
                                        {t("Telegram")} :{" "}
                                        <a href={`https://t.me/${coingeckoData?.links?.telegram_channel_identifier}`} target="_blank" rel="noopener noreferrer">
                                            {coingeckoData?.links?.telegram_channel_identifier}
                                        </a>
                                    </p>
                                )}
                                {coingeckoData?.links?.official_forum_url?.[0] && (
                                    <p>
                                        {t("Instagram")} :{" "}
                                        <a href={coingeckoData?.links?.official_forum_url[0]} target="_blank" rel="noopener noreferrer">
                                            {coingeckoData?.links?.official_forum_url[0]}
                                        </a>
                                    </p>
                                )}
                                {coingeckoData?.links?.official_forum_url?.[1] && (
                                    <p>
                                        {t("CoinMarketCap")} :{" "}
                                        <a href={coingeckoData?.links?.official_forum_url[1]} target="_blank" rel="noopener noreferrer">
                                            {coingeckoData?.links?.official_forum_url[1]}
                                        </a>
                                    </p>
                                )}
                            </div>

                            {/* <div className="d-flex align-items-center justify-content-between pb-1">
                                <h2 className="bs-card-header-title">{t("Trading Pairs")}</h2>
                            </div>
                            <div className="table-responsive pb-2">
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>{t("#")}</th>
                                            <th>{t("Exchange")}</th>
                                            <th>{t("Pair")}</th>
                                            <th>{t("Price")}</th>
                                            <th>{t("Spread")}</th>
                                            <th>{t("24h Volume")}</th>
                                            <th>{t("Volume %")}</th>
                                            <th>{t("Last Updated")}</th>
                                            <th>{t("Trust Score")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tickers.map((ticker: any, index: number) => (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    {ticker.market.name}
                                                </td>
                                                <td>
                                                    <a href={ticker.trade_url} target="_blank" rel="noopener noreferrer">
                                                        {ticker.base}/{ticker.target}
                                                    </a>
                                                </td>
                                                <td>{getFiat(ticker.converted_last.usd, 6, fiat, true)}</td>
                                                <td>{ticker.bid_ask_spread_percentage?.toFixed(2)}%</td>
                                                <td>{getFiat(ticker.converted_volume.usd, 6, fiat, true)}</td>
                                                <td>{((ticker.converted_volume.usd / totalVolume) * 100).toFixed(2)}%</td>
                                                <td>{ticker.last_fetch_at ? formatDistanceToNow(new Date(ticker.last_fetch_at)) : 'N/A'}</td>
                                                <td>
                                                    <span className={`badge ${ticker.trust_score === 'green' ? 'bg-success' : ticker.trust_score === 'yellow' ? 'bg-warning' : 'bg-danger'}`}>
                                                        {ticker.trust_score}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div> */}
                        </div>
                    ) : (
                        <div className="bs-card-body p-3">
                            <div className="py-2">
                                <p>
                                    {t("Click here to")}{" "}
                                    <Link to="/contactus?id=4">{t("update")}</Link>&nbsp;{" "}
                                    {t("the token information / general information")}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Info;
